// ============================================================================
// ProgressArc — an animated circular progress ring with a centred value.
// Used here for the share of income that has been PAID. Reduced-motion aware
// (snaps to final), draws on scroll-into-view, gradient stroke per accent.
// ============================================================================
import { useId } from "react";
import type { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { clamp } from "../format";
import type { ChartAccent } from "./TrendChart";

const ACCENTS: Record<ChartAccent, { from: string; to: string }> = {
  coral: { from: "#FF5E62", to: "#FF9966" },
  violet: { from: "#6A5BFF", to: "#9D6BFF" },
  ocean: { from: "#2D8FFF", to: "#5B6BFF" },
  lime: { from: "#18C964", to: "#3BE085" },
};

export interface ProgressArcProps {
  /** 0–100. Clamped. */
  pct: number;
  accent?: ChartAccent;
  size?: number;
  stroke?: number;
  /** Centre content (e.g. the big % number). */
  children?: ReactNode;
  ariaLabel?: string;
}

/** A circular progress ring; the arc sweeps to `pct` on view. */
export function ProgressArc({
  pct,
  accent = "lime",
  size = 132,
  stroke = 12,
  children,
  ariaLabel,
}: ProgressArcProps) {
  const reduce = useReducedMotion();
  const uid = useId();
  const colors = ACCENTS[accent];

  const value = clamp(pct, 0, 100);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - value / 100);

  return (
    <div
      className="relative inline-grid place-items-center"
      style={{ width: size, height: size }}
      role="img"
      aria-label={ariaLabel}
    >
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id={`${uid}-arc`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={colors.from} />
            <stop offset="100%" stopColor={colors.to} />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#EEF1F8"
          strokeWidth={stroke}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={`url(#${uid}-arc)`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          initial={reduce ? { strokeDashoffset: offset } : { strokeDashoffset: c }}
          whileInView={{ strokeDashoffset: offset }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: reduce ? 0 : 1.1, ease: [0.22, 0.61, 0.36, 1] }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        {children}
      </div>
    </div>
  );
}
